import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { derivedBodies, UNRATEABLE_BLIND_SPOT } from './derivedBodies.js'
import { REGISTRY_ENTRIES, REGISTRY_LABELS, REGISTRY_ENTITY_COUNT } from '../../lib/bodyRegistryIndex.js'
/* The two tables below used to render `aria-disabled="true"` — flagged, with
   the door nailed shut. These give them doors. See the notes at each table. */
import { pageIssues } from './bodyIssues.js'
import IssueFix from './IssueFix.jsx'
import styles from './DerivedBodies.module.css'

/**
 * A CARD FOR EVERY BODY THE OBJECT NAMES — the tab that ends the Pac-12 defect.
 *
 * The other five tabs on this page are drawn from the registry. This one is
 * drawn from the records, so a body cannot be invisible merely because nobody
 * has written it down yet: measured 2026-10-14 off a full page-through of all
 * 15,854 records, 467 labels carrying 3,077 records had no card anywhere in the
 * product, and they have one here.
 *
 * ⚠️ IT DOES NOT USE `matcherFor()`, AND THAT IS THE POINT. Every other card
 * on this page joins its records with a name-and-acronym regex, which is why
 * "CAA Football" can collect NCAA football titles and why `\bAAA\b` once put
 * 318 championships on Arkansas's card. These cards are an EXACT PARTITION of
 * the object on the raw label, so each record lands on exactly one card and the
 * arithmetic at the top can close. A fuzzy join here would make it unprovable.
 */
const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')

/* ⚠️ ASSERTED SENTENCES AS NAMED CONSTANTS. check-shipped-features.mjs greps
 * the BUILT bundle and the JSX transform splits multi-line element text, so only
 * a named constant survives the build as one literal. Same reason
 * BodyCoverage.jsx and GoverningBodies.jsx do it. */
export const DERIVED_HEADLINE = 'bodies are carded from the object itself, not from the registry'
export const DERIVED_RECONCILED =
  'Every record in the object is on exactly one card above or in exactly one flagged row, so '
  + 'nothing is stranded.'
export const DERIVED_RECONCILE_FAILED = 'RECONCILIATION FAILED.'
export const DERIVED_NO_CROWN_CLAIM =
  'A card here says this label is NAMED on these records. It does not say the body crowns them.'
export const DERIVED_UNASSIGNED_IS_A_FINDING =
  'Unassigned on a card here is a finding, not a defect being hidden: these records could not '
  + 'reach a rep before because they could not reach a card.'
export const DERIVED_FUSED_BLOCKED =
  'BLOCKED, not missing. One key carries more than one jurisdiction, so no single card can '
  + 'honestly represent it and none has been created.'

export default function DerivedBodies({ records = [], complete = true, loading = false, focusBody = '' }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(focusBody || null)
  useEffect(() => { if (focusBody) { setOpen(focusBody); setOnly('all') } }, [focusBody])
  const [only, setOnly] = useState('all')
  /* Which fused / mis-carded row has its fix panel open. One at a time: two
     open splits on colliding acronyms is how the wrong one gets applied. */
  const [openFix, setOpenFix] = useState(null)

  const d = useMemo(
    () => derivedBodies(records, {
      entries: REGISTRY_ENTRIES, labels: REGISTRY_LABELS, entityCount: REGISTRY_ENTITY_COUNT,
    }),
    [records],
  )

  /* ⚠️ BUILT FROM THE SAME `d`, so the controls act on exactly the rows the
     tables above print. Deriving them from a second call would let the table
     and its door disagree about which records are fused — which is the
     reconciliation failure this whole surface warns about, one level down. */
  /* ⚠️ FROM `records`, NOT FROM `d.fusedKeys`. `derivedBodies` partitions on
     `sr_conference` alone, so a record carrying a fused string in
     `governing_body` never reaches its fused table — measured 2026-10-16,
     that is 82 records (MHSAA 18, IHSAA 29, CCAA 35) the table reported as
     zero. `pageIssues` scans both fields against the registry's own
     AMBIGUOUS / FUSED_ON_OBJECT sets, so the control covers what the table
     below cannot see as well as what it can. */
  const fusedIssues = useMemo(
    () => pageIssues({ records }).filter((i) => i.kind === 'fused-key'),
    [records],
  )
  const misCardedIssues = useMemo(
    () => pageIssues({ derived: { fusedKeys: [], misCarded: d.misCarded } }).filter((i) => i.kind === 'mis-carded'),
    [d],
  )

  if (loading) {
    return (
      <section className="card">
        <p>Loading the object. Nothing is carded until the walk finishes — a partial load
          would undercount the bodies with no registry entry, which is the reassuring
          direction to be wrong in.</p>
      </section>
    )
  }

  const t = d.totals
  const shown = only === 'all' ? d.cards : d.cards.filter((c) => c.bucket === only)
  const sum = (a) => a.reduce((n, r) => n + r.count, 0)

  const card = (r) => {
    const isOpen = open === r.body
    const b = d.buckets.find((x) => x.key === r.bucket)
    return (
      <div key={r.body} data-derived-body={r.body} tabIndex={-1} className={styles.card} data-bucket={r.bucket} data-open={isOpen ? '1' : '0'}>
        <button type="button" className={styles.head} aria-expanded={isOpen}
          onClick={() => setOpen(isOpen ? null : r.body)}>
          <span className={styles.name}>{r.body}</span>
          <span className={styles.count}>
            {/* ⚠️ A CARD THAT IS ENTIRELY UNRATEABLE DOES NOT LEAD WITH A TOTAL.
                "21 records" that are all placeholder text is a new kind of lie. */}
            {r.unrateable === r.count
              ? <em className={styles.danger}>{nf(r.count)} records, none ring-worthy</em>
              : <>{nf(r.count)} records{r.unrateable > 0 && <em className={styles.warn}> · {nf(r.unrateable)} unrateable</em>}</>}
          </span>
          <span className={styles.bucketTag} data-bucket={r.bucket}>{b?.short || r.bucket}</span>
          <span className={styles.rep}>
            {r.reps.length
              ? r.reps.map((x) => `${x.rep} (${nf(x.count)})`).join(', ')
              : <em className={styles.unassigned}>Unassigned</em>}
          </span>
          <span className={styles.chev}>{isOpen ? '▾' : '▸'}</span>
        </button>
        {isOpen && (
          <div className={styles.detail}>
            <p className={styles.why}><b>{b?.label}.</b> {b?.why} <b>Fix:</b> {b?.fix}</p>
            {r.alias && (
              <p className={styles.note}>
                The registry already holds <b>{r.alias.label}</b> — matched on {r.alias.how}.
                Nothing to acquire here.
              </p>
            )}
            {r.substringRisk && (
              <p className={styles.warn}>
                ⚠ This label also sits INSIDE the registry label “{r.substringRisk}” with no
                token agreement. Flagged, never merged: that is the shape that let
                “CAA Football” collect NCAA football titles, and a word-boundary regex
                cannot catch it because the boundary falls inside a word.
              </p>
            )}
            <p className={styles.meta}>
              {r.crowned > 0
                ? <><b>{nf(r.crowned)}</b> carry a champion. </>
                : 'No champion is recorded on any of these. '}
              {r.states.length
                ? <>States on the records: {r.states.join(', ')}. </>
                : 'No state is recorded on any of these. '}
              {r.segments.length > 1 && <>Segments: {r.segments.map(([s, n]) => `${s} (${nf(n)})`).join(', ')}. </>}
              {r.governingBodies.length > 0 && (
                <>governing_body on these records: {r.governingBodies.slice(0, 4).map(([g, n]) => `${g} (${nf(n)})`).join(', ')}
                  {r.governingBodies.length > 4 && ` and ${r.governingBodies.length - 4} more`}. </>
              )}
              {r.unrateableKinds.length > 0 && (
                <>Unrateable: {r.unrateableKinds.map(([k, n]) => `${k} (${nf(n)})`).join(', ')}. </>
              )}
              {/* ⚠️ "ANOTHER LINE", NOT "ANOTHER BOOK". check-ownership-language
                  caught the first wording, correctly: nobody at SR has a book of
                  their own, a rep holds an assignment for a period. The same
                  phrase is already on the removed-defects list in
                  check-shipped-features.mjs from when Body Coverage shipped it. */}
              {r.outOfScope && <><b>Its top segment is routed to another line,</b> so this is not an assignment gap. </>}
            </p>
            {r.truncated && (
              <p className={styles.meta}>Showing the first {nf(r.records.length)} of {nf(r.count)} records.</p>
            )}
            {/* The wrapper is what keeps the PAGE from scrolling sideways on a
                phone — see .tableWrap. */}
            <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Championship</th><th>Sport</th><th>State</th><th>Date</th><th>Champion</th></tr>
              </thead>
              <tbody>
                {r.records.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {/* The same `?record=` door Body Coverage and MyWorklist use.
                          A second drawer here would be a second thing to drift. */}
                      <button type="button" className={styles.openRecord}
                        onClick={() => navigate(`/command-center?record=${encodeURIComponent(c.id)}`)}>
                        {c.name} →
                      </button>
                    </td>
                    <td>{c.sport || <span className={styles.muted}>—</span>}</td>
                    <td>{c.state || <span className={styles.muted}>—</span>}</td>
                    <td>
                      {c.dateRaw || <span className={styles.muted}>none</span>}
                      {c.dateRaw && !c.dateTrusted && <span className={styles.tag}> projected</span>}
                    </td>
                    <td>{c.champion || <span className={styles.muted}>not crowned</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <section className={`card ${styles.headline}`}>
        <div className={styles.big}>{nf(t.derivedCards)}</div>
        <div className={styles.bigLabel}>
          {DERIVED_HEADLINE}, covering <b>{nf(t.derivedRecords)}</b> records that no card on the
          other five tabs could hold. Each one opens.
        </div>
        {!complete && (
          <p className={styles.warn} role="alert">
            THE OBJECT IS NOT FULLY LOADED, so these are undercounts. Reload before acting on them.
          </p>
        )}
        <p className={styles.note}>{DERIVED_NO_CROWN_CLAIM}</p>
      </section>

      {/* ---- THE ARITHMETIC, BEFORE THE CARDS ------------------------------ */}
      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>Cards plus flagged equals every record</h2>
        <table className={styles.recon}>
          <tbody>
            <tr>
              <td className={styles.n}>{nf(t.onCard)}</td>
              <td>on a registry card — <b>{nf(t.onCardBodies)}</b> labels reach one of the{' '}
                {REGISTRY_ENTITY_COUNT} registry entities</td>
            </tr>
            <tr data-flag="1">
              <td className={styles.n}>{nf(t.derivedRecords)}</td>
              <td>on a card DERIVED FROM THE OBJECT — <b>{nf(t.derivedCards)}</b> labels the
                registry does not hold. These had no card before this tab.</td>
            </tr>
            <tr>
              <td className={styles.n}>{nf(t.unspecified)}</td>
              <td>the record says <code>Unspecified</code>. Not an organisation, so no card and
                no registry entry can ever help. <b>Fix: the data.</b></td>
            </tr>
            <tr>
              <td className={styles.n}>{nf(t.blank)}</td>
              <td>the governing-body field is empty — kept apart from <code>Unspecified</code>{' '}
                because one was typed and the other never was. <b>Fix: the data.</b></td>
            </tr>
            <tr className={styles.sum}>
              <td className={styles.n}>{nf(t.accounted)}</td>
              <td>accounted for, of <b>{nf(t.universe)}</b> records loaded.</td>
            </tr>
          </tbody>
        </table>
        {d.reconciled ? (
          <p className={styles.ok}>Reconciled. {DERIVED_RECONCILED}</p>
        ) : (
          <p className={styles.fail} role="alert">
            <strong>{DERIVED_RECONCILE_FAILED}</strong> {nf(Math.abs(d.shortfall))} records
            are {d.shortfall > 0 ? 'missing from' : 'double-counted across'} the rows above.
            Do not act on any count on this tab until that is explained.
          </p>
        )}
        <p className={styles.note}>
          <b>{nf(t.noRepDerivedBodies)}</b> of these cards carry no rep on any record
          ({nf(t.noRepDerivedRecords)} records). {DERIVED_UNASSIGNED_IS_A_FINDING}
        </p>
      </section>

      {/* ---- WHAT THE 467 ACTUALLY ARE -------------------------------------- */}
      <section className={`card ${styles.section}`}>
        <h2 className={styles.h2}>They are not {nf(t.derivedCards)} missing governing bodies</h2>
        <p className={styles.note}>
          Four different populations hide in that number and they need four different fixes.
          Creating a registry row for all of them would create hundreds of entries for
          promoters, spelling variants and placeholder text.
        </p>
        <table className={styles.recon}>
          <tbody>
            {/* ⚠️ aria-disabled BECAUSE IT IS A READOUT, which is what
                check-clickable asks a row to declare. The record rows below DO
                get a destination; these three tables are counts, and the chips
                under them are the control that acts on a bucket. */}
            {/* ⚠️ THE UNRATEABLE ROW RENDERS EVEN AT ZERO, AND ONLY THAT ONE.
                An empty bucket is normally noise, but a bucket that reads zero
                BECAUSE THE SURFACE CANNOT SEE ITS BIGGEST CLASS is the silent
                zero this page exists to end. So it keeps its row and carries
                the reason; the others are hidden when empty. */}
            {/* ⚠️ `data-readout="summary"` IS AN EXPLICIT OPT-OUT, NOT A
                SYNONYM FOR aria-disabled. check-no-dead-ends.mjs fails any
                aria-disabled row on this surface, because that attribute is
                exactly how the fused-key and mis-carded tables shipped
                flagged-with-the-door-shut. This row is different and has to
                say so: it restates bucket counts whose control is the chip
                bar in the next section, and the gate only accepts the opt-out
                in a file that renders real controls elsewhere. */}
            {d.buckets.filter((b) => b.bodies > 0 || b.key === 'unrateable').map((b) => (
              <tr key={b.key} data-bucket={b.key} aria-disabled="true" data-readout="summary">
                <td className={styles.n}>{nf(b.bodies)}</td>
                <td>
                  <b>{b.label}</b> — {nf(b.records)} records
                  {b.unrateable > 0 && <>, {nf(b.unrateable)} of them unrateable</>}
                  {b.noRepBodies > 0 && <>, {nf(b.noRepBodies)} cards unassigned</>}.{' '}
                  <span className={styles.muted}>{b.fix}</span>
                  {b.key === 'unrateable' && (
                    <><br /><span className={styles.warn}>{UNRATEABLE_BLIND_SPOT}</span></>
                  )}
                </td>
              </tr>
            ))}
            <tr className={styles.sum}>
              <td className={styles.n}>{nf(d.buckets.reduce((n, b) => n + b.bodies, 0))}</td>
              <td>classified, of <b>{nf(t.derivedCards)}</b> cards —{' '}
                {nf(d.buckets.reduce((n, b) => n + b.records, 0))} records of{' '}
                <b>{nf(t.derivedRecords)}</b>.</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* ---- THE FUSED KEYS — BLOCKED, WITH THE REASON --------------------- */}
      {(d.fusedKeys.length > 0 || fusedIssues.length > 0) && (
        <section className={`card ${styles.section}`} data-bucket="fused">
          {/* ⚠️ THE HEADING COUNTS `fusedIssues`, NOT `d.fusedKeys`, and the two
              differ on purpose. `d.fusedKeys` is the sr_conference-only view and
              read ZERO on 2026-10-16 while 82 records sat on fused strings in
              `governing_body`. A section that renders nothing because it looked
              at one field is a silent zero, which is the thing this tab exists
              to end. */}
          <h2 className={styles.h2}>
            {nf(fusedIssues.length)} keys carry more than one jurisdiction —{' '}
            {nf(fusedIssues.reduce((n, i) => n + (i.count || 0), 0))} records
          </h2>
          <p className={styles.warn}>{DERIVED_FUSED_BLOCKED}</p>
          {/* Every fused key, with its split, whichever field carries it. */}
          {fusedIssues.map((i) => (
            <IssueFix key={i.id} issue={i} context={{ records: i.records }} />
          ))}
          {/* ⚠️ THE sr_conference-ONLY TABLE THAT USED TO STAND HERE IS GONE,
              and that is a deletion with a reason rather than a tidy-up. It
              rendered `aria-disabled="true"` rows under the comment "A readout,
              and deliberately NOT a door: there is nothing to open, because no
              card was created for a fused key." Both halves were true and the
              conclusion was inverted — there is nothing to OPEN, and plenty to
              DO, which is how WIAA, MHSAA (public), IHSAA (public) and CAA went
              to zero on 2026-10-15. It also read off one field and so reported
              zero while 82 records sat on fused strings in `governing_body`.
              `fusedIssues` above is a strict superset of what it showed, and
              every row of it carries the split. */}
          <p className={styles.note}>
            These sit on <code>governing_body</code>, which the card join does not read, so they
            are NOT among the {nf(t.derivedCards)} above and are not double-counted. They are
            listed because a surface that said nothing about them would be hiding the repair.
          </p>
        </section>
      )}

      {/* ---- AND A FALSE MATCH THAT IS ALREADY LIVE ------------------------ */}
      {d.misCarded.length > 0 && (
        <section className={`card ${styles.section}`}>
          <h2 className={styles.h2}>
            {nf(d.misCarded.length)} labels reach a card only by dropping their qualifier —{' '}
            {nf(t.misCardedRecords)} records
          </h2>
          <p className={styles.warn}>
            ⚠ These count as covered today and at least one is on the WRONG card. The card
            lookup falls back to the stripped acronym, which is right for most labels and wrong
            wherever the acronym means two bodies. Named, not fixed: re-pointing a card is a
            data decision, and merging on the stripped acronym would collapse distinct body
            pairs.
          </p>
          <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Label on the record</th><th>Records</th><th>Card it reaches</th><th>States</th><th>Fix</th></tr></thead>
            <tbody>
              {/* ⚠️ THE OLD COMMENT HERE READ: "A readout. Re-pointing one of
                  these cards is a data decision, so there is deliberately no
                  control here that would make it." The premise is right and
                  the conclusion inverted it: BECAUSE it is a data decision, a
                  human needs a control — automation must not make it, and the
                  person who can is the one reading this table. The control
                  below writes the qualified body string the object already
                  uses, as the signed-in person, with a reason on the ledger. */}
              {d.misCarded.map((m) => {
                const iss = misCardedIssues.find((i) => i.bodyKey === m.body)
                const isOpen = openFix === `miscarded:${m.body}`
                return (
                  <Fragment key={m.body}>
                    <tr>
                      <td><code>{m.body}</code></td>
                      <td>{nf(m.count)}</td>
                      <td><code>{m.card?.key || m.card?.card}</code></td>
                      <td>{m.states.join(', ') || <span className={styles.muted}>none</span>}</td>
                      <td>
                        <button type="button" className={styles.openRecord}
                          onClick={() => setOpenFix(isOpen ? null : `miscarded:${m.body}`)}>
                          {isOpen ? 'Close' : 'Re-point these →'}
                        </button>
                      </td>
                    </tr>
                    {isOpen && iss && (
                      <tr><td colSpan={5}><IssueFix issue={iss} context={{ records: iss.records }} /></td></tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
          </div>
        </section>
      )}

      {/* ---- THE CARDS ----------------------------------------------------- */}
      <section className={`card ${styles.section}`}>
        <div className={styles.filterBar} role="group" aria-label="Which population to show">
          <button type="button" className={styles.chip} data-on={only === 'all' ? '1' : '0'}
            onClick={() => setOnly('all')}>All {nf(d.cards.length)}</button>
          {d.buckets.filter((b) => b.bodies > 0).map((b) => (
            <button key={b.key} type="button" className={styles.chip} data-on={only === b.key ? '1' : '0'}
              onClick={() => setOnly(b.key)}>{b.label} {nf(b.bodies)}</button>
          ))}
        </div>
        <p className={styles.note}>
          Showing <b>{nf(shown.length)}</b> cards, <b>{nf(sum(shown))}</b> records. Ranked by how
          much is behind them.
        </p>
        <div className={styles.cards}>{shown.map(card)}</div>
      </section>
    </>
  )
}
